import { Icon } from '@polyseme/design-system';
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import React from 'react';
import Marquee from 'react-fast-marquee';

import { images } from '~/landing/assets';

import { Title } from '../components/title';
import { Typography } from '../components/typography';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const testimonials = [
  {
    text: 'Polysème est simple, fiable et efficace. Les améliorations régulières répondent à nos besoins et à nos exigences spécifiques. L’outil permet à nos gestionnaires de travailler plus efficacement et de gagner en productivité au quotidien.',
    author: 'Anne-Marie D.',
    role: 'Responsable pôle Gestion',
    logo: {
      src: images.polyseme.integrations['garance'],
      alt: 'Garance',
    },
  },
  {
    text: "Grâce à Polysème, j'ai pu simplifier mes parcours de sourcing et gagner énormément de temps au quotidien.",
    author: 'Olivier E.',
    role: 'Conseiller mutualiste',
  },
  {
    text: 'Un vrai partenaire au quotidien : Polysème nous accompagne dans nos process et nous permet de délivrer plus vite.',
    author: 'Sophie R.',
    role: 'Directrice Digital',
  },
] as const;

const longestTestimonialText = testimonials.reduce((longest, current) =>
  current.text.length > longest.text.length ? current : longest
).text;

export const Testimony = () => {
  const t = useTranslations('landing.polyseme.testimonials');

  return (
    <motion.section
      className="relative w-full bg-white py-[60px]"
      initial="hidden"
      transition={{ duration: 0.5, ease: 'easeOut' }}
      variants={fadeInUp}
      viewport={{ once: true, amount: 0.2 }}
      whileInView="visible"
    >
      <Title
        as="h2"
        className="mb-10 text-center text-gray-900"
        variant="title-2"
      >
        {t('title')}
      </Title>

      {/* eslint-disable-next-line tailwindcss/enforces-negative-arbitrary-values */}
      <div className="relative inset-x-1/2 -mx-[50vw] w-screen overflow-hidden">
        <Marquee pauseOnHover speed={100}>
          {[...testimonials, ...testimonials, ...testimonials].map(
            (testimonial, i) => (
              <article
                key={i}
                className="relative mx-4 flex w-[320px] shrink-0 flex-col rounded-3xl bg-gray-50 p-10 shadow-sm sm:w-[500px] md:w-[600px]"
              >
                {/* Hidden content for height reference - longest testimonial */}
                <Typography
                  aria-hidden="true"
                  as="blockquote"
                  className="font-jomolhari invisible mb-6 text-lg text-black md:text-3xl"
                >
                  &ldquo;{longestTestimonialText}&rdquo;
                </Typography>

                {/* Visible content positioned absolutely */}
                <Typography
                  as="blockquote"
                  className="font-jomolhari absolute inset-x-10 top-10 mb-6 text-lg text-black md:text-3xl"
                >
                  &ldquo;{testimonial.text}&rdquo;
                </Typography>

                <div className="mt-auto flex items-center gap-4">
                  <div className="flex size-20 shrink-0 items-center justify-center rounded-full bg-white">
                    {'logo' in testimonial ? (
                      <Image
                        alt={testimonial.logo.alt}
                        className="size-8 object-contain"
                        height={32}
                        src={testimonial.logo.src}
                        width={32}
                      />
                    ) : (
                      <Icon
                        className="size-8 text-[#B5BFFB]"
                        name="user-round"
                      />
                    )}
                  </div>
                  <div>
                    <Typography className="mb-1 font-semibold text-foreground">
                      {testimonial.author}
                    </Typography>
                    <Typography className="text-muted-foreground">
                      {testimonial.role}
                    </Typography>
                  </div>
                </div>
              </article>
            )
          )}
        </Marquee>
      </div>
    </motion.section>
  );
};
